import {
  CaseReducer,
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { TimePreference, OrgIntegrationService } from 'sb-lib/types';
import {
  Credentials,
  OnboardingStep,
  Org,
  OrgInvite,
  OrgRole,
  OrgStatus,
  SelfAccount,
  SelfPublicAccount,
  SelfUser,
  User,
  UserAccounType,
  UserGroup,
  UserOnboarding,
} from 'types';
import { RouteProps } from 'react-router-dom';
import {
  resendEmailVerification as apiResendEmailVerification,
  updateUser as apiUpdateUser,
  verifyEmail as apiVerifyEmail,
  getUserOnboardingState,
  joinOrgFromInvite,
  logoutAccount,
  markOnboardingStepComplete,
  updateAccountEmail,
  putUserTimePreferences,
  getUserGroups,
} from 'shared/api';
import { AppState } from './reducers';
import { RejectError } from 'shared/api/axios';
import {
  addDomain,
  authAccount,
  ssoAuthAccount,
  authImpersonateAccount,
  changeActiveOrg,
  checkAccount,
  createAccount,
  createOrg,
  removeDomain,
  removeMember,
  saveClearbitIntegration,
  saveOrgIntegration,
  updateOrgIntegration,
  removeOrgIntegration,
  saveUserIntegration,
  removeUserIntegration,
  updateUserIntegration,
  updateTeamMember,
  updateOrgSettings,
  setAccount,
  setNoAccount,
  saveChiliPiperIntegration,
  updateRequireSecureIdentities,
  saveSixsenseIntegration,
  saveHubSpotMeetingsIntegration,
  salesfloorLinkedInSignin,
  refreshOrgSettings,
} from './shared-actions';
import {
  getStoredActiveOrgId,
  setStoredActiveOrgId,
  setStoredAuthenticationToken,
} from './storage';
import { addManyCases } from 'shared/utils/redux';
import {
  selectActiveUser,
  internalSelectActiveUser,
  selectActivePublicUser,
} from './shared-selectors';
import { integrationData } from 'shared/utils/integrations';
import { mediaDevices } from 'shared/utils/media-devices';

// STATE
export const initialState = {
  account: null as SelfAccount | null,
  publicAccount: null as SelfPublicAccount | null,
  userGroups: null as UserGroup[] | null,
  activeOrgId: getStoredActiveOrgId(),
  isAuthingUser: false,
  authUserError: null as string | null,

  authForwardLocation: undefined as RouteProps['location'],

  isCreatingUser: false,
  createUserError: null as string | null,

  isCheckingUser: false,
  hasCheckedUser: false,
  lastCheckTime: 0,

  isLoggingOut: false,

  isSigningAuth: false,
  signAuthError: null as string | null,

  isChangingEmail: false,
  changeEmailError: null as string | null,

  invite: null as OrgInvite | null,

  isUpdatingUser: false,
  hasUpdatedUser: false,
  updateUserError: null as string | null,

  isResendingEmailVerification: false,
  hasResentEmailVerification: false,
  resendEmailVerificationError: null as string | null,

  isVerifyingEmail: false,
  hasVerifiedEmail: false,
  verifyEmailError: null as string | null,

  needsVerification: false,

  isCompletingOnboardingStep: false,
  hasCompletedOnboardingStep: false,
  completeOnboardingStepError: null as string | null,

  hasSavedUserIntegration: false,
  isSavingUserIntegrations: false,
  saveUserIntegrationError: null as null | string,
};

export type AuthState = typeof initialState;

// THUNKS
export const changeEmail = createAsyncThunk<
  User['emailAddress'],
  Credentials,
  { rejectValue: RejectError }
>('auth/changeEmail', async ({ emailAddress, password }) => {
  const res = await updateAccountEmail(emailAddress, password);
  if (res.data.authenticationToken) {
    setStoredAuthenticationToken(res.data.authenticationToken);
  }
  return emailAddress;
});

export const updateUser = createAsyncThunk<
  SelfUser,
  Partial<SelfUser>,
  { state: AppState; rejectValue: RejectError }
>('auth/updateUser', async (user, thunkApi) => {
  const activeUser = selectActiveUser(thunkApi.getState());
  const args = {
    id: activeUser?.id,
    ...user,
  };
  const res = await apiUpdateUser(args);

  if (user.settings?.videoEffect !== undefined) {
    await mediaDevices.setVideoEffect(
      user.settings?.videoEffect,
      user.settings?.videoBackgroundImageId,
    );
  }

  return res.data;
});

export const updateUserTimePreferences = createAsyncThunk<
  TimePreference[],
  TimePreference[],
  { state: AppState; rejectValue: RejectError }
>('auth/updateUserTimePreferences', async timePreferences => {
  const res = await putUserTimePreferences(timePreferences);
  return res.data;
});

export const logout = createAsyncThunk<void, void, {}>('auth/logout', async () => {
  // no need to wait for api logout
  await logoutAccount();
  // start logout before removing token
  setStoredAuthenticationToken(null);
});

export const verifyEmail = createAsyncThunk<void, string>(
  'auth/verifyEmail',
  async (code: string) => {
    await apiVerifyEmail(code);
  },
);

export const resendEmailVerification = createAsyncThunk<
  void,
  void,
  { state: AppState; rejectValue: RejectError }
>('auth/verifyEmailResend', async () => {
  await apiResendEmailVerification();
});

export const completeOnboardingStep = createAsyncThunk<UserOnboarding, OnboardingStep>(
  'auth/completeOnboardingStep',
  async step => {
    const res = await markOnboardingStepComplete(step);
    return res.data;
  },
);

export const refreshOnboarding = createAsyncThunk<
  UserOnboarding,
  void,
  { state: AppState; rejectValue: RejectError }
>('auth/refreshOnboarding', async () => {
  const res = await getUserOnboardingState();
  return res.data;
});

export const joinOrg = createAsyncThunk<
  SelfUser,
  Parameters<typeof joinOrgFromInvite>[0],
  { state: AppState; rejectValue: RejectError }
>('auth/joinOrg', async (args, { dispatch }) => {
  const res = await joinOrgFromInvite(args);
  dispatch(checkAccount());
  return res.data;
});

export const loadGroups = createAsyncThunk<
  UserGroup[],
  void,
  { state: AppState; rejectValue: RejectError }
>('auth/loadGroups', async () => {
  const res = await getUserGroups();
  return res.data;
});

// SLICE
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthForwardLocation(state, action: PayloadAction<RouteProps['location']>) {
      state.authForwardLocation = action.payload;
    },
    clearChangeEmailError(state) {
      state.changeEmailError = null;
    },
    setInvite(state, action: PayloadAction<OrgInvite>) {
      state.invite = action.payload;
    },
    clearVerifyEmailEphemeralState(state) {
      state.isVerifyingEmail = false;
      state.hasVerifiedEmail = false;
      state.verifyEmailError = null;
    },
    clearResendEmailVerificationEphemeralState(state) {
      state.isResendingEmailVerification = false;
      state.hasResentEmailVerification = false;
      state.resendEmailVerificationError = null;
    },
  },
  extraReducers: builder => {
    // Shared helpers
    const updateActiveUser = (state: AuthState, updates: Partial<SelfUser>) => {
      if (!state.account) return;
      const activeUserIndex = Math.max(
        state.account.users.findIndex(u => u.org.id === state.activeOrgId),
        0,
      );
      state.account.users[activeUserIndex] = {
        ...state.account.users[activeUserIndex],
        ...updates,
      };
    };

    const updateAccountUser = (state: AuthState, updatedUser: SelfUser) => {
      if (!state.account) return;
      state.account.users = state.account.users.map(u => {
        if (u.id === updatedUser.id) {
          return updatedUser;
        }
        return u;
      });
    };

    // loadGroups
    builder.addCase(loadGroups.fulfilled, (state, action) => {
      state.userGroups = action.payload;
    });

    // checkUser
    builder.addCase(checkAccount.pending, state => {
      state.isCheckingUser = true;
    });
    addManyCases(builder, [checkAccount.fulfilled, setAccount], (state, action) => {
      if (action.payload.type === UserAccounType.PUBLIC) {
        state.publicAccount = action.payload;
        state.account = null;
        // For Public users we know exactly, which user is signed in, so we can set their Org ID
        // for the frontend to correctly pick the right user
        if (action.payload.authedUserOrgId) {
          state.activeOrgId = action.payload.authedUserOrgId;
        }
      } else {
        state.account = action.payload;
        state.publicAccount = null;
        const activeUser = internalSelectActiveUser(state);
        mediaDevices.setVideoEffect(
          activeUser?.settings.videoEffect || null,
          activeUser?.settings.videoBackgroundImage?.id,
        );
      }
      state.isCheckingUser = false;
      state.hasCheckedUser = true;
      state.lastCheckTime = Date.now();
    });
    addManyCases(builder, [checkAccount.rejected, setNoAccount], state => {
      state.isCheckingUser = false;
      state.hasCheckedUser = true;
      state.account = null; // remove user on failure (401)
      state.publicAccount = null;
    });

    // authAccount & ssoAuthAccount & authImpersonateAccount
    addManyCases(
      builder,
      [authAccount.pending, ssoAuthAccount.pending, authImpersonateAccount.pending],
      state => {
        state.account = null;
        state.isAuthingUser = true;
        state.authUserError = null;
      },
    );
    addManyCases(
      builder,
      [authAccount.fulfilled, ssoAuthAccount.fulfilled, authImpersonateAccount.fulfilled],
      (state, action) => {
        state.account = action.payload;
        state.isAuthingUser = false;
      },
    );
    addManyCases(
      builder,
      [authAccount.rejected, ssoAuthAccount.rejected, authImpersonateAccount.rejected],
      (state, action) => {
        state.isAuthingUser = false;
        state.authUserError = action.error?.message || null;
      },
    );

    builder.addCase(salesfloorLinkedInSignin.pending, state => {
      state.authUserError = null;
      state.publicAccount = null;
      state.activeOrgId = null;
    });
    builder.addCase(salesfloorLinkedInSignin.rejected, state => {
      state.publicAccount = null;
      state.activeOrgId = null;
      state.authUserError =
        'Could not sign you in with LinkedIn. Please try again later.';
    });

    // createUser
    builder.addCase(createAccount.pending, state => {
      state.isCreatingUser = true;
      state.createUserError = null;
    });
    builder.addCase(createAccount.fulfilled, (state, action) => {
      state.account = action.payload;
      state.isCreatingUser = false;
    });
    builder.addCase(createAccount.rejected, (state, action) => {
      state.isCreatingUser = false;

      state.createUserError = action?.payload?.message || action.error.message || null;

      if (action?.payload?.existing) {
        state.needsVerification = true;
      }
    });

    // changeEmail
    builder.addCase(changeEmail.pending, state => {
      state.isChangingEmail = true;
      state.changeEmailError = null;
    });
    builder.addCase(changeEmail.fulfilled, (state, action) => {
      state.isChangingEmail = false;
      updateActiveUser(state, { emailAddress: action.payload });
    });
    builder.addCase(changeEmail.rejected, (state, action) => {
      state.isChangingEmail = false;
      state.changeEmailError = action.error.message || null;
    });

    // logout
    builder.addCase(logout.pending, state => {
      state.isLoggingOut = true;
      state.account = null;
      state.publicAccount = null;
    });
    builder.addCase(logout.fulfilled, state => {
      state.isLoggingOut = false;
      state.account = null;
      state.publicAccount = null;
    });
    builder.addCase(logout.rejected, state => {
      state.isLoggingOut = false;
      state.account = null;
      state.publicAccount = null;
    });

    // updateUser
    builder.addCase(updateUser.pending, state => {
      state.isUpdatingUser = true;
      state.hasUpdatedUser = false;
      state.updateUserError = null;
    });
    builder.addCase(updateUser.fulfilled, (state, action) => {
      state.isUpdatingUser = false;
      state.hasUpdatedUser = true;
      updateAccountUser(state, action.payload);
    });
    builder.addCase(updateUser.rejected, (state, action) => {
      state.isUpdatingUser = false;
      state.updateUserError = action.error.message || null;
    });

    // resendEmailVerification
    builder.addCase(resendEmailVerification.pending, state => {
      state.isResendingEmailVerification = true;
      state.resendEmailVerificationError = null;
      state.hasResentEmailVerification = false;
    });
    builder.addCase(resendEmailVerification.fulfilled, state => {
      state.isResendingEmailVerification = false;
      state.hasResentEmailVerification = true;
    });
    builder.addCase(resendEmailVerification.rejected, (state, action) => {
      state.isResendingEmailVerification = false;
      state.resendEmailVerificationError = action.error.message || null;
    });

    // verifyEmail
    builder.addCase(verifyEmail.pending, state => {
      state.isVerifyingEmail = true;
      state.verifyEmailError = null;
      state.hasVerifiedEmail = false;
    });
    builder.addCase(verifyEmail.fulfilled, state => {
      state.isVerifyingEmail = false;
      state.hasVerifiedEmail = true;
      updateActiveUser(state, { emailVerified: true });
    });
    builder.addCase(verifyEmail.rejected, (state, action) => {
      state.isVerifyingEmail = false;
      state.verifyEmailError = action.error.message || null;
    });

    // completeOnboardingStep
    builder.addCase(completeOnboardingStep.pending, state => {
      state.isCompletingOnboardingStep = true;
      state.completeOnboardingStepError = null;
      state.hasCompletedOnboardingStep = false;
    });
    builder.addCase(completeOnboardingStep.fulfilled, (state, action) => {
      state.isCompletingOnboardingStep = false;
      state.hasCompletedOnboardingStep = true;
      updateActiveUser(state, { onboarding: action.payload });
    });
    builder.addCase(completeOnboardingStep.rejected, (state, action) => {
      state.isCompletingOnboardingStep = false;
      state.completeOnboardingStepError = action.error.message || null;
    });

    // refreshOnboarding
    builder.addCase(refreshOnboarding.fulfilled, (state, action) => {
      updateActiveUser(state, { onboarding: action.payload });
    });

    // updateUserTimePreferences
    builder.addCase(updateUserTimePreferences.fulfilled, (state, action) => {
      updateActiveUser(state, { timePreferences: action.payload });
    });

    // createOrg
    builder.addCase(createOrg.fulfilled, (state, action) => {
      if (state.account) {
        state.account.users = [...state.account.users, action.payload];
      }
    });

    // selectActiveOrg
    builder.addCase(changeActiveOrg, (state, action) => {
      state.activeOrgId = action.payload;
      // TODO: Consider redux-persist?
      setStoredActiveOrgId(action.payload);
    });

    // saveUserIntegration
    builder.addCase(saveUserIntegration.pending, state => {
      state.isSavingUserIntegrations = true;
      state.hasSavedUserIntegration = false;
      state.saveUserIntegrationError = null;
    });
    builder.addCase(saveUserIntegration.fulfilled, (state, action) => {
      state.isSavingUserIntegrations = false;
      state.hasSavedUserIntegration = true;
      updateActiveUser(state, { integrations: action.payload });
    });
    builder.addCase(saveUserIntegration.rejected, (state, action) => {
      state.isSavingUserIntegrations = false;
      state.saveUserIntegrationError = action.error.message || null;
    });

    // removeUserIntegration
    builder.addCase(removeUserIntegration.fulfilled, (state, action) => {
      updateActiveUser(state, { integrations: action.payload });
    });

    // updateUserIntegration
    builder.addCase(updateUserIntegration.fulfilled, (state, action) => {
      updateActiveUser(state, { integrations: action.payload });
    });

    /* Org actions update user.org as well */
    const handleUserOrgUpdate: CaseReducer<AuthState, PayloadAction<Org>> = (
      state,
      action,
    ) => {
      updateActiveUser(state, { org: action.payload });
    };
    addManyCases(
      builder,
      [
        saveOrgIntegration.fulfilled,
        saveClearbitIntegration.fulfilled,
        saveSixsenseIntegration.fulfilled,
        saveChiliPiperIntegration.fulfilled,
        saveHubSpotMeetingsIntegration.fulfilled,
        refreshOrgSettings.fulfilled,
      ],
      handleUserOrgUpdate,
    );
    builder.addCase(removeOrgIntegration.fulfilled, handleUserOrgUpdate);
    builder.addCase(updateOrgIntegration.fulfilled, handleUserOrgUpdate);
    builder.addCase(updateOrgSettings.fulfilled, handleUserOrgUpdate);
    builder.addCase(updateRequireSecureIdentities.fulfilled, handleUserOrgUpdate);

    builder.addCase(updateTeamMember.fulfilled, (state, action) => {
      const user = internalSelectActiveUser(state);
      if (!user) return;
      user.org.users = user.org.users.map(u => {
        if (u.id === action.payload.id) {
          return action.payload;
        }
        return u;
      });
    });

    builder.addCase(addDomain.fulfilled, (state, action) => {
      const user = internalSelectActiveUser(state);
      if (!user) return;
      user.org.domains = [...user.org.domains, action.payload];
    });
    builder.addCase(removeDomain.fulfilled, (state, action) => {
      const user = internalSelectActiveUser(state);
      if (!user) return;
      user.org.domains = user.org.domains.filter(d => d.id !== action.meta.arg);
    });

    builder.addCase(removeMember.fulfilled, (state, action) => {
      const user = internalSelectActiveUser(state);
      if (!user) return;
      user.org.users = user.org.users.filter(u => u.id !== action.meta.arg);
    });
  },
});

// SELECTORS

export const selectActiveOrg = (s: AppState) => selectActiveUser(s)?.org;
export const selectActivePublicOrg = (s: AppState) => selectActivePublicUser(s)?.org;

export const selectAccountOrgs = createSelector(
  (state: AppState) => state.auth.account,
  account => account?.users.map(u => u.org) || [],
);

export const selectActiveOrgMembers = createSelector(
  selectActiveOrg,
  org => org?.users || [],
);

export const selectUserGroups = (s: AppState) => s.auth.userGroups;

export const selectOrgMember = createSelector(
  [selectActiveOrgMembers, (_: AppState, userId: number | undefined) => userId],
  (users, userId) => (userId ? users.find(u => u.id === userId) : undefined),
);

const fakeZapierIntegration = {
  service: OrgIntegrationService.zapier,
  serviceId: '',
  data: {},
  stale: false,
};

export const selectActiveOrgIntegrations = createSelector(selectActiveOrg, org => [
  ...(org?.integrations || []),
  fakeZapierIntegration,
]);

export const selectActiveSlackIntegration = createSelector(selectActiveOrg, org => {
  const integrations = [...(org?.integrations || []), fakeZapierIntegration];

  return integrations.find(i => i.service === OrgIntegrationService.slack);
});

export const selectActiveUserIntegrations = createSelector(
  selectActiveUser,
  user => user?.integrations || [],
);

export const selectActiveCalendarIntegrations = createSelector(
  selectActiveUserIntegrations,
  // Optional chaining, so that we don't break if server returned integration frontend doesn't know about
  integrations => integrations.filter(i => integrationData[i.service]?.isCalendar),
);

export const selectActiveCrmIntegrations = createSelector(
  selectActiveOrgIntegrations,
  // Optional chaining, so that we don't break if server returned integration frontend doesn't know about
  integrations => integrations.filter(i => integrationData[i.service]?.isCrm),
);

export const selectActiveOrgDomains = createSelector(
  selectActiveOrg,
  org => org?.domains || [],
);

export const selectIsOnboarded = (s: AppState) =>
  selectActiveOrg(s)?.status === OrgStatus.ACTIVE;

export const selectOrgMaxSessions = (state: AppState) => {
  const org = selectActiveOrg(state);
  if (!org) {
    return 0;
  }
  return org.plan.maxSessions;
};

export const selectShowOrgSelector = (s: AppState) => {
  const orgs = selectAccountOrgs(s);
  if (orgs.length > 1) return true;
  return !!orgs.find(o => !!o.plan.multiOrg);
};

export const selectIsAdmin = (s: AppState) =>
  !!selectActiveUser(s)?.orgRoles.includes(OrgRole.ADMIN);

export { selectActiveUser } from './shared-selectors';

export const selectCanRecordSessions = (s: AppState) => {
  const org = selectActiveOrg(s);
  return org && !org.hippaMode && org.plan.sessionRecording && org.allowRecording;
};

export const selectCanDisplayshare = (s: AppState) => {
  const org = selectActiveOrg(s);
  return !!org && !!org.plan.displayshare;
};

export const selectCanDisplaydemo = (s: AppState) => {
  const org = selectActiveOrg(s);
  return !!org && !!org.plan.displaydemo;
};

// EXPORTS
export const {
  setAuthForwardLocation,
  clearChangeEmailError,
  setInvite,
  clearVerifyEmailEphemeralState,
  clearResendEmailVerificationEphemeralState,
} = authSlice.actions;
export { authAccount, createAccount, checkAccount };
export default authSlice.reducer;
